/**
 * Subscription Page
 * Main subscription management page
 * 
 * Simple logic:
 * 1. Get organizationId from URL param or user profile
 * 2. Check if subscription exists
 * 3. Show dashboard or redirect to onboarding
 */

import React, {useEffect, useState} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import SubscriptionDashboard from '../components/subscription/SubscriptionDashboard';
import SubscriptionLayout from '../components/subscription/SubscriptionLayout';
import {useAuth} from '../contexts/AuthContext';
import {getSubscription} from '../services/SubscriptionService';
import {hasActiveSubscription} from '../utils/planLimits';

const SubscriptionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {user} = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    const loadSubscription = async () => {
      if (!user) {
        navigate('/admin/login');
        return;
      }

      // Simple: Get organizationId from URL param or user profile
      const orgFromUrl = searchParams.get('org');
      const orgFromUser = user.organizationId;
      
      // Use URL param if provided, otherwise use user's organizationId
      const orgId = orgFromUrl || orgFromUser || null;
      
      if (!orgId) {
        // No organizationId - user needs to complete onboarding
        navigate('/onboarding');
        return;
      }

      setOrganizationId(orgId);

      try {
        // Check if subscription exists
        const subscription = await getSubscription(orgId);
        
        if (!subscription || !hasActiveSubscription(subscription)) {
          // No active subscription - redirect to onboarding
          navigate('/onboarding');
          return;
        }
        
        // Has active subscription - show dashboard
        setIsChecking(false);
      } catch (error) {
        console.error('[SubscriptionPage] Error loading subscription:', error);
        // On error, redirect to onboarding
        navigate('/onboarding');
      }
    };

    loadSubscription();
  }, [user, searchParams, navigate]);

  if (isChecking || !organizationId) {
    return (
      <SubscriptionLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading subscription...</p>
          </div>
        </div>
      </SubscriptionLayout>
    );
  }

  return (
    <SubscriptionLayout>
      <SubscriptionDashboard organizationId={organizationId} />
    </SubscriptionLayout>
  );
};

export default SubscriptionPage;
